import {
  SCHEMA_VERSION,
  VERSION_CONSENTIMIENTO,
  type EvaluacionControl,
  type OrdenVisitaDTO,
  type ResultadoControl,
} from '@ausentia/shared';
import type { CapturaFirma } from '../componentes/Firma.js';
import type { PerfilMedico } from '../contexto/Sesion.js';
import { capturarGeo, descripcionDispositivo, estaOnline, evidenciaContingencia } from '../almacen/red.js';
import { uuid } from '../almacen/cripto.js';
import { encolarControl, sincronizar } from '../almacen/outbox.js';

/**
 * Estado en memoria del control que el médico está cargando (orden → formulario
 * → consentimiento → firma). Se arma el payload del Anexo B recién al confirmar.
 */
export interface BorradorControl {
  orden: OrdenVisitaDTO;
  resultado: ResultadoControl | null;
  evaluacion?: EvaluacionControl;
  motivoNoRealizada?: string;
  consentimientos?: {
    devolucionRecibida: boolean;
    datosPersonales: boolean;
    textoVersion: string;
    envio: { email: boolean; whatsapp: boolean };
  };
  /** referencia a adjuntos locales (ids de Dexie) ya capturados in-app. */
  adjuntosLocales?: {
    id: string;
    tipo: 'CONSTANCIA_PAPEL_FOTO' | 'FACHADA_FOTO' | 'EVIDENCIA_FOTO';
    hashSha256: string;
    capturadaEn: string;
  }[];
  contingenciaPapel?: boolean;
  rectificaA?: string;
}

const BORRADOR_KEY = 'ausentia_borrador_v1';
let borrador: BorradorControl | null = null;

export function iniciarBorrador(b: BorradorControl) {
  borrador = b;
  sessionStorage.setItem(BORRADOR_KEY, JSON.stringify(b));
}
export function borradorActual(): BorradorControl | null {
  if (borrador) return borrador;
  try {
    const raw = sessionStorage.getItem(BORRADOR_KEY);
    if (raw) borrador = JSON.parse(raw) as BorradorControl;
  } catch {
    sessionStorage.removeItem(BORRADOR_KEY);
  }
  return borrador;
}
export function actualizarBorrador(parcial: Partial<BorradorControl>) {
  if (borrador) {
    borrador = { ...borrador, ...parcial };
    sessionStorage.setItem(BORRADOR_KEY, JSON.stringify(borrador));
  }
}
export function limpiarBorrador() {
  borrador = null;
  sessionStorage.removeItem(BORRADOR_KEY);
}

export interface PayloadArmado {
  payload: Record<string, unknown>;
  idempotencyKey: string;
  adjuntosLocales: string[];
  contingencia: boolean;
  resumen: string;
}

/** Construye el payload app→backend (schemaVersion vigente) desde el borrador. */
export async function armarPayload(
  b: BorradorControl,
  medico: PerfilMedico,
  firma: CapturaFirma | null,
): Promise<PayloadArmado> {
  if (!b.resultado) throw new Error('No se ha seleccionado el resultado del control');
  const geo = await capturarGeo();
  const offline = !estaOnline();
  const contingenciaActiva = offline || !!b.contingenciaPapel;
  const idempotencyKey = uuid();

  const adjuntos = (b.adjuntosLocales ?? []).map((a) => ({
    tipo: a.tipo,
    uploadId: a.id, // se reemplaza por el uploadId real del servidor al sincronizar
    hashSha256: a.hashSha256,
    capturadaEn: a.capturadaEn,
    paginas: 1,
  }));

  const payload: Record<string, unknown> = {
    schemaVersion: SCHEMA_VERSION,
    idempotencyKey,
    orden: {
      ordenId: b.orden.ordenId,
      empresaClienteId: b.orden.empresa.empresaId,
      tipoControl: b.orden.tipoControl,
    },
    resultado: b.resultado,
    profesional: {
      profesionalId: medico.id,
      matricula: {
        tipo: medico.matricula.tipo,
        numero: medico.matricula.numero,
        jurisdiccion: medico.matricula.jurisdiccion,
      },
    },
    trabajador: { trabajadorId: b.orden.trabajador.trabajadorId, dni: b.orden.trabajador.dni },
    firmaProfesional: { modo: 'REMOTA_POR_OPERACION', proveedorId: medico.firmaProveedor },
    geo,
    dispositivo: {
      plataforma: 'web',
      osVersion: '-',
      appVersion: '1.0.0',
      deviceIdHash: 'web-demo',
      integridad: { mecanismo: 'webDemo', veredicto: 'NO_EVALUADO', evaluadoEn: new Date().toISOString() },
    },
    contingencia: {
      activa: contingenciaActiva,
      evidenciaConectividad: contingenciaActiva
        ? { ...evidenciaContingencia(), dispositivo: descripcionDispositivo() }
        : null,
      adjuntos,
    },
  };

  if (b.rectificaA) payload.rectificaA = b.rectificaA;

  if (b.resultado === 'REALIZADA') {
    if (b.evaluacion) payload.evaluacion = b.evaluacion;
    if (firma) {
      payload.firmaTrabajador = {
        tipo: 'GRAFOMETRICA',
        imagenPng: firma.imagenPngBase64,
        trazos: firma.trazos,
        capturadaEn: new Date().toISOString(),
        duracionMs: firma.duracionMs,
        cantidadTrazos: firma.cantidadTrazos,
        hashSha256: firma.hashSha256,
      };
      payload.consentimientos = b.consentimientos ?? {
        devolucionRecibida: true,
        datosPersonales: true,
        textoVersion: VERSION_CONSENTIMIENTO,
        envio: { email: true, whatsapp: false },
      };
    }
  } else if (b.motivoNoRealizada) {
    payload.motivoNoRealizada = b.motivoNoRealizada;
  }

  const res = b.resultado!;
  const resumen =
    res === 'REALIZADA'
      ? `Control · ${b.orden.trabajador.nombreCompleto}`
      : `${res.replace(/_/g, ' ').toLowerCase()} · ${b.orden.trabajador.nombreCompleto}`;

  return {
    payload,
    idempotencyKey,
    adjuntosLocales: (b.adjuntosLocales ?? []).map((a) => a.id),
    contingencia: contingenciaActiva,
    resumen,
  };
}

export type DestinoEnvio = 'firma' | 'acta' | 'pendientes' | 'bloqueada';
export interface ResultadoEnvio {
  destino: DestinoEnvio;
  codigo?: string;
  challengeId?: string;
  metodoFirma?: string;
  mensaje?: string;
}

/**
 * Encola el control en el outbox y sincroniza si hay red. Devuelve a dónde
 * navegar según la respuesta del servidor (firma OTP, acta de contingencia,
 * cola de pendientes o firma bloqueada por REFEPS).
 */
export async function enviarBorrador(
  medico: PerfilMedico,
  firma: CapturaFirma | null,
): Promise<ResultadoEnvio> {
  const b = borradorActual();
  if (!b) throw new Error('No hay un control en curso');
  const armado = await armarPayload(b, medico, firma);
  const tipo: 'CONTROL' | 'CONTROL_PAPEL' | 'EXCEPCION' =
    b.resultado !== 'REALIZADA' ? 'EXCEPCION' : b.contingenciaPapel ? 'CONTROL_PAPEL' : 'CONTROL';

  const itemId = await encolarControl({
    ordenId: b.orden.ordenId,
    idempotencyKey: armado.idempotencyKey,
    tipo,
    payload: armado.payload,
    adjuntosLocales: armado.adjuntosLocales,
    resumen: armado.resumen,
    contingencia: armado.contingencia,
  });

  const resultados = await sincronizar();
  const mio = resultados.find((r) => r.itemId === itemId);
  limpiarBorrador();

  if (!mio) return { destino: 'pendientes' }; // sin red: quedó encolado
  if (!mio.ok) return { destino: 'pendientes', mensaje: mio.error };
  const resp = mio.respuesta!;
  if (resp.actaRequerida) return { destino: 'acta', codigo: resp.codigo };
  if (resp.estado === 'FIRMA_BLOQUEADA')
    return { destino: 'bloqueada', codigo: resp.codigo, mensaje: resp.motivoBloqueo };
  if (resp.challenge) return { destino: 'firma', codigo: resp.codigo, challengeId: resp.challenge.challengeId, metodoFirma: resp.challenge.metodo };
  return { destino: 'pendientes', codigo: resp.codigo };
}
